/**
 * Success card shown after the new version is saved.
 * Shows version number with navigation options.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface Props {
  versionNumber: number | null;
  onStartOver: () => void;
}

const VersionCreatedCard: React.FC<Props> = ({ versionNumber, onStartOver }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col items-center gap-5 rounded-xl border border-border/60 bg-card p-8 text-center max-w-md mx-auto"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
        <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-heading">
          {versionNumber ? `Version ${versionNumber} Created` : 'New Version Created'}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Your new prompt version has been saved with the staging label. You can review it on the Versions page.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onStartOver}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Create Another
        </Button>
        <Button
          size="sm"
          onClick={() => navigate('/agents')}
        >
          View Versions
          <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </div>
    </motion.div>
  );
};

export default VersionCreatedCard;
